export type ControlMode = 'keyboard' | 'gamepad' | 'touch';
export type ControlAction = 'up' | 'down' | 'left' | 'right' | 'pass' | 'shoot' | 'punch' | 'sprint';
export type GamepadAction = ControlAction | 'pause';

export interface PlayerControlSettings {
  mode: ControlMode;
  keys: Record<ControlAction, string>;
  buttons: Record<GamepadAction, number>;
  padIndex: number;
  axisX: number;
  axisY: number;
  invertX: boolean;
  invertY: boolean;
  deadzone: number;
}

export type ControlSettings = [PlayerControlSettings, PlayerControlSettings];

export const CONTROL_SETTINGS_STORAGE_KEY = 'futbol-fighters-controls-v1';
export const CONTROL_ACTIONS: readonly ControlAction[] = ['up', 'down', 'left', 'right', 'pass', 'shoot', 'punch', 'sprint'];
export const GAMEPAD_ACTIONS: readonly GamepadAction[] = ['pass', 'shoot', 'punch', 'sprint', 'pause', 'up', 'down', 'left', 'right'];
export const CONTROL_ACTION_LABELS: Record<GamepadAction, string> = {
  up: 'Arriba', down: 'Abajo', left: 'Izquierda', right: 'Derecha',
  pass: 'Pase', shoot: 'Tiro', punch: 'Ataque', sprint: 'Cambiar jugador',
  pause: 'Pausa / Start',
};
const STANDARD_BUTTON_LABELS = ['A / Cruz', 'B / Círculo', 'X / Cuadrado', 'Y / Triángulo', 'LB / L1', 'RB / R1', 'LT / L2', 'RT / R2', 'Select', 'Start', 'L3', 'R3', 'Cruceta arriba', 'Cruceta abajo', 'Cruceta izquierda', 'Cruceta derecha'];
export const GAMEPAD_BUTTON_LABELS = Array.from({ length: 64 }, (_, index) => `${index}: ${STANDARD_BUTTON_LABELS[index] ?? 'Botón ' + index}`);

export function defaultControlSettings(): ControlSettings {
  const touch = (navigator.maxTouchPoints ?? 0) > 0 || matchMedia('(pointer: coarse)').matches;
  const pad = () => ({ padIndex: -1, axisX: 0, axisY: 1, invertX: false, invertY: false, deadzone: 0.24, buttons: { pass: 0, shoot: 1, punch: 2, sprint: 3, pause: 9, up: 12, down: 13, left: 14, right: 15 } });
  return [
    { ...pad(), mode: touch ? 'touch' : 'keyboard', keys: { up: 'KeyW', down: 'KeyS', left: 'KeyA', right: 'KeyD', pass: 'KeyJ', shoot: 'KeyK', punch: 'KeyL', sprint: 'Space' } },
    { ...pad(), mode: 'keyboard', keys: { up: 'ArrowUp', down: 'ArrowDown', left: 'ArrowLeft', right: 'ArrowRight', pass: 'Numpad1', shoot: 'Numpad2', punch: 'Numpad3', sprint: 'Numpad0' } },
  ];
}

export function normalizeControlSettings(value: unknown): ControlSettings {
  const defaults = defaultControlSettings();
  if (!Array.isArray(value)) return defaults;
  return [0, 1].map(player => {
    const saved = value[player] && typeof value[player] === 'object' ? value[player] as Partial<PlayerControlSettings> : {};
    const keys = saved.keys && typeof saved.keys === 'object' ? saved.keys as Partial<Record<ControlAction, unknown>> : {};
    const buttons = saved.buttons && typeof saved.buttons === 'object' ? saved.buttons as Partial<Record<GamepadAction, unknown>> : {};
    const mode: ControlMode = saved.mode === 'gamepad' || saved.mode === 'touch' ? saved.mode : 'keyboard';
    return {
      mode,
      padIndex: Number.isInteger(saved.padIndex) && saved.padIndex! >= -1 && saved.padIndex! < 16 ? saved.padIndex! : -1,
      axisX: Number.isInteger(saved.axisX) && saved.axisX! >= 0 && saved.axisX! < 16 ? saved.axisX! : 0,
      axisY: Number.isInteger(saved.axisY) && saved.axisY! >= 0 && saved.axisY! < 16 ? saved.axisY! : 1,
      invertX: saved.invertX === true,
      invertY: saved.invertY === true,
      deadzone: typeof saved.deadzone === 'number' && Number.isFinite(saved.deadzone) ? Math.min(0.8, Math.max(0.05, saved.deadzone)) : 0.24,
      keys: Object.fromEntries(CONTROL_ACTIONS.map(action => {
        const key = keys[action];
        return [action, typeof key === 'string' && /^[A-Za-z0-9]+$/.test(key) && key.length <= 24 ? key : defaults[player].keys[action]];
      })) as PlayerControlSettings['keys'],
      buttons: Object.fromEntries(GAMEPAD_ACTIONS.map(action => {
        const button = buttons[action];
        return [action, typeof button === 'number' && Number.isInteger(button) && button >= 0 && button < GAMEPAD_BUTTON_LABELS.length ? button : defaults[player].buttons[action]];
      })) as PlayerControlSettings['buttons'],
    };
  }) as ControlSettings;
}

export function loadControlSettings(): ControlSettings {
  try { return normalizeControlSettings(JSON.parse(localStorage.getItem(CONTROL_SETTINGS_STORAGE_KEY) ?? 'null')); }
  catch { return defaultControlSettings(); }
}
